/**
 * Article rédigé le 2026-09-06 à partir de sources web ouvertes et vérifiées automatiquement
 * (7 URL testées, révisé après re-vérification). Daté au moment des faits : 2026-01-12.
 * À relire avant/après mise en ligne. Format : voir src/blog/README.md
 */
export default {
  "slug": "ces-2026-startups-bourgogne-franche-comte-las-vegas",
  "title": "CES 2026 : dix start-up de Bourgogne-Franche-Comté à Las Vegas",
  "excerpt": "Du 6 au 9 janvier, la Région, la CCI et La French Tech BFC ont emmené une délégation au CES de Las Vegas. Dix start-up y exposaient : ce que l'on sait du dispositif régional, de son financement — et de ce qu'il ne dit pas.",
  "seoTitle": "CES 2026 : dix start-up de BFC à Las Vegas",
  "seoDescription": "Du 6 au 9 janvier 2026, dix start-up de Bourgogne-Franche-Comté exposaient au CES de Las Vegas avec la Région, la CCI et La French Tech : dispositif et coûts.",
  "category": "startups",
  "tags": [
    "CES 2026",
    "French Tech BFC",
    "Las Vegas",
    "Hors Besançon",
    "TEMIS"
  ],
  "author": "jimmy",
  "publishedAt": "2026-01-12",
  "updatedAt": "2026-01-12",
  "featured": false,
  "cover": {
    "image": "/img/blog/ces-2025-lvcc-01.jpg",
    "credit": "Photo : Las Vegas Convention Center West Hall pendant le CES 2025 — Xuthoria, CC BY-SA 4.0, via Wikimedia Commons",
    "alt": "Façade vitrée du Las Vegas Convention Center West Hall pendant le CES, avec une immense bannière bleue « CES » et des visiteurs devant l'entrée"
  },
  "content": [
    {
      "type": "lead",
      "text": "Du 6 au 9 janvier 2026, le Consumer Electronics Show de Las Vegas a de nouveau accueilli l'une des plus importantes délégations françaises de son histoire récente. La Bourgogne-Franche-Comté y a envoyé dix start-up exposantes, au sein d'une délégation de dix-huit entreprises que la Région présente comme sa plus fournie depuis cinq ans. Reste la question que se posent toutes les jeunes pousses du bassin bisontin : à quoi sert réellement ce voyage, et à quel prix ?"
    },
    {
      "type": "keyfacts",
      "facts": [
        {
          "label": "Quoi",
          "value": "CES 2026, salon mondial de l'électronique grand public et de l'innovation"
        },
        {
          "label": "Quand",
          "value": "Du 6 au 9 janvier 2026"
        },
        {
          "label": "Où",
          "value": "Las Vegas, l'essentiel des start-up françaises réunies à Eureka Park, au Venetian"
        },
        {
          "label": "Qui",
          "value": "Région Bourgogne-Franche-Comté, CCI BFC, AER BFC, La French Tech BFC et Business France"
        },
        {
          "label": "Pour qui",
          "value": "Dix start-up régionales exposantes, huit autres entreprises en learning expedition"
        }
      ]
    },
    {
      "type": "h2",
      "text": "Dix exposantes dans une French Tech qui se stabilise"
    },
    {
      "type": "p",
      "text": "La Région et ses partenaires — CCI Bourgogne-Franche-Comté, [La French Tech BFC](/blog/french-tech-bfc-capitale-2026-2028-besancon) et Business France — ont accompagné une délégation de dix-huit entreprises régionales, dont dix start-up exposantes[^1]. Le décompte varie selon les canaux : la communication régionale relayée par la presse locale, qui associe également l'AER BFC comme facilitateur économique, évoque une délégation de dix-huit personnes et treize entreprises, dont les dix start-up[^2]. L'écart n'a pas été explicité : La French Tech BFC compte dix-huit entreprises embarquées, quand la presse régionale décrit dix-huit personnes et treize entreprises. Les deux décomptes n'ont pas été rapprochés."
    },
    {
      "type": "p",
      "text": "Le contexte national, lui, est plus nuancé qu'il n'y paraît. La France a aligné une centaine de start-up sur environ cent cinquante entreprises présentes, dont cinquante-quatre regroupées à Eureka Park, au Venetian, et une dizaine dans le West Hall. Elle reste la première délégation européenne, selon Business France, tandis que l'Italie, les Pays-Bas et la Suisse renforcent la leur. Le contingent, lui, s'effrite légèrement : elles étaient cent dix en 2025, un niveau que *Maddyness* décrit comme quasiment équivalent. Autre signal, quatre exposantes françaises sur cinq avaient déjà fait le déplacement par le passé, là où l'édition précédente était largement composée de primo-exposantes[^3]. Le CES français se professionnalise et se stabilise à un niveau bas."
    },
    {
      "type": "quote",
      "text": "une étape clé pour nouer des partenariats, tester des usages et ouvrir de nouveaux marchés",
      "cite": "Silvère Denis, directeur délégué de La French Tech Bourgogne-Franche-Comté[^1]"
    },
    {
      "type": "h2",
      "text": "Ce que les dix sont allées présenter"
    },
    {
      "type": "p",
      "text": "La liste publiée par La French Tech BFC donne une photographie assez fidèle de l'écosystème régional : peu de matériel lourd, beaucoup de logiciel et d'intelligence artificielle appliquée à des métiers précis[^1]."
    },
    {
      "type": "ul",
      "items": [
        "**Abeye** — aide à la lecture pour les personnes dyslexiques, fondée sur la technologie Lexilens",
        "**CoreFator** — logiciels de gestion pour cliniques vétérinaires et suivi des animaux de compagnie",
        "**OHAIO** — interprétation en temps réel, chacun s'exprimant dans sa langue",
        "**Dynaverse** — expériences 3D immersives directement dans le navigateur, sans téléchargement",
        "**DynaTech** — suite de gestion d'activité avec l'ERP modulable Daily",
        "**Toolkit** — pilotage de chantiers : avancement, coordination des équipes, rentabilité",
        "**Spotnevents** — application recensant événements et spots touristiques dans le monde",
        "**Postmaker** — IA marketing pour la restauration, publication automatisée de contenus",
        "**Intellectuality** — solution Numexia, agents LLM appliqués à l'analyse financière",
        "**5Discovery** — parcours de formation immersifs en réalité virtuelle enrichis par l'IA"
      ]
    },
    {
      "type": "p",
      "text": "À ces dix exposantes s'ajoutaient huit entreprises inscrites en *learning expedition* : pas de stand, mais un programme de décryptage des tendances, de benchmark et de rendez-vous, pensé comme une préparation à une future présence sur les grands salons[^1]."
    },
    {
      "type": "h2",
      "text": "Le coût, l'accompagnement et ce qu'on peut en attendre"
    },
    {
      "type": "p",
      "text": "L'opération régionale proposait plusieurs formules, selon que l'entreprise venait observer, prospecter ou exposer, avec une organisation clé en main couvrant vols, hébergement, transferts, badges d'accès et accompagnement sur place ; les inscriptions étaient closes depuis le 30 septembre 2025[^4]. Un repère utile pour les candidates des prochaines éditions : pour 2026, le dossier devait être bouclé fin septembre, soit plus de trois mois avant l'ouverture."
    },
    {
      "type": "p",
      "text": "Côté financement, la participation s'inscrit dans le programme régional des actions collectives à l'international, doté d'un million d'euros par la Région et qui accompagne chaque année environ cinq cents entreprises dans l'industrie, la santé, le luxe, l'agroalimentaire et le numérique[^2]. Le reste à charge par entreprise, lui, n'est pas rendu public : ni la Région ni La French Tech BFC ne communiquent de tarif de stand. Le salon lui-même est annoncé par les organisateurs de la mission à plus de 130 000 visiteurs professionnels et 4 000 exposants internationaux[^4]. À cette échelle, se rendre visible suppose un budget qu'aucune des parties prenantes n'a chiffré publiquement — c'est précisément ce que la communication régionale ne dit pas."
    },
    {
      "type": "p",
      "text": "Quant aux retombées, la prudence s'impose à cette date : aucune distinction obtenue par l'une des dix exposantes régionales n'a été annoncée par la Région ou par La French Tech BFC dans les jours suivant la clôture du salon, et les bilans chiffrés de contacts ne sont pas encore publiés."
    },
    {
      "type": "h2",
      "text": "Et Besançon dans tout cela ?"
    },
    {
      "type": "p",
      "text": "Les villes d'implantation ne figurent pas dans le communiqué de la délégation, mais l'annuaire en ligne de La French Tech BFC les précise fiche par fiche : CoreFator à Prémery, Dynaverse à Guérigny, Spotnevents à Challuy, DynaTech à Fleury-sur-Loire et 5Discovery à Nevers, toutes dans la Nièvre ; Toolkit à Martigny-le-Comte, en Saône-et-Loire ; Intellectuality à Saffres et Abeye à Beaune, en Côte-d'Or[^7]. Deux exposantes, OHAIO et Postmaker, n'y sont pas localisées. Aucune adresse bisontine n'apparaît parmi les huit fiches consultables."
    },
    {
      "type": "p",
      "text": "Sur les huit exposantes dont l'annuaire régional donne l'adresse, aucune n'est franc-comtoise : cinq sont installées dans la Nièvre, une en Saône-et-Loire, deux en Côte-d'Or — dont Abeye, à Beaune, incubée par le réseau Atol les Opticiens, qui avait décroché un Innovation Award dès sa première participation au CES, en 2020[^5]. La délégation exposante 2026 est donc, pour ce qu'on en sait, entièrement bourguignonne."
    },
    {
      "type": "p",
      "text": "Ce n'est pas une fatalité, et le précédent le plus parlant est bisontin. En 2024, [SilMach](/blog/micronora-2024-microns-or-jumeau-numerique), deeptech bisontine, avait remporté la plus haute distinction des CES Innovation Awards — « Best innovation award, 1e place » selon [TEMIS](/blog/temis-technopole-startups-deeptech-besancon) — dans la catégorie technologies embarquées, pour son micromoteur à cœur de silicium, présenté comme le plus petit et le plus autonome au monde[^6]. Quand une technologie du bassin bisontin est allée à Las Vegas, elle y a trouvé un jury. C'est notre lecture : la microtechnique et les dispositifs médicaux, cœur de compétence de TEMIS, sont des domaines qui trouvent leur public au CES — SilMach en reste l'exemple le plus souvent cité."
    },
    {
      "type": "callout",
      "tone": "tip",
      "title": "Ce qu'il faut retenir",
      "text": "Le CES n'est pas un guichet à contrats : c'est un test de marché accéléré, un exercice de pitch en anglais et une mise en concurrence directe avec des milliers d'exposants. Pour une start-up du bassin bisontin, la question n'est pas « faut-il y aller » mais « avec quel produit démontrable, quel objectif chiffré et quelle capacité à relancer les contacts en février »."
    },
    {
      "type": "h2",
      "text": "Le vrai test commence après Las Vegas"
    },
    {
      "type": "p",
      "text": "La délégation 2026 confirme une trajectoire : l'écosystème régional dispose d'un vivier suffisant pour aligner une délégation d'une année sur l'autre, et d'assez de diversité pour couvrir la santé animale, la formation immersive, la finance augmentée ou l'accessibilité. Ce qu'elle ne dit pas encore, c'est ce que ces quatre jours produiront en chiffre d'affaires, en levées ou en partenariats industriels. La réponse se lira dans les mois à venir — et, du point de vue bisontin, dans la capacité de la Franche-Comté à figurer dans une délégation exposante dont les huit implantations connues sont toutes bourguignonnes."
    }
  ],
  "references": [
    {
      "id": 1,
      "title": "CES Las Vegas 2026 : découvrez la délégation de start-up Bourgogne-Franche-Comté",
      "publisher": "La French Tech Bourgogne-Franche-Comté",
      "url": "https://lafrenchtechbfc.fr/actualites/ecosysteme/ces-2026-la-french-tech-bourgogne-franche-comte-embarque-une-delegation-de-start-up-a-las-vegas/",
      "type": "official",
      "date": "2025-12-10"
    },
    {
      "id": 2,
      "title": "La Bourgogne-Franche-Comté au CES de Las Vegas pour soutenir ses entreprises",
      "publisher": "macommune.info",
      "url": "https://www.macommune.info/la-bourgogne-franche-comte-au-ces-de-las-vegas-pour-soutenir-ses-entreprises/",
      "type": "press",
      "date": "2026-01-06"
    },
    {
      "id": 3,
      "title": "CES 2026 : une centaine de startups vont représenter la French Tech à Las Vegas",
      "publisher": "Maddyness",
      "url": "https://www.maddyness.com/2026/01/05/ces-2026-une-centaine-de-startups-vont-representer-la-french-tech-a-las-vegas/",
      "type": "press",
      "date": "2026-01-05"
    },
    {
      "id": 4,
      "title": "CES Las Vegas 2026 — mission collective régionale",
      "publisher": "La French Tech Bourgogne-Franche-Comté",
      "url": "https://lafrenchtechbfc.fr/nos-evenements/ces-las-vegas-2026/",
      "type": "official"
    },
    {
      "id": 5,
      "title": "Beaune : la start-up Abeye présente au CES sa nouvelle innovation développée sur la base de la technologie Lexilens",
      "publisher": "Info Beaune",
      "url": "https://www.info-beaune.com/articles/2024/01/10/5578/beaune-la-start-up-abeye-presente-au-ces-sa-nouvelle-innovation-developpee-sur-la-base-de-la-technologie-lexilens/",
      "type": "press",
      "date": "2024-01-10"
    },
    {
      "id": 6,
      "title": "CES 2024 : Best Innovation Award pour SilMach",
      "publisher": "TEMIS Technopole Besançon",
      "url": "https://www.temis.org/ces-2024-best-innovation-award-pour-silmach/",
      "type": "official",
      "date": "2024-02-28"
    },
    {
      "id": 7,
      "title": "Annuaire des organisations de La French Tech Bourgogne-Franche-Comté",
      "publisher": "La French Tech Bourgogne-Franche-Comté",
      "url": "https://annuaire.lafrenchtechbfc.fr/",
      "type": "official"
    }
  ]
};
